import type {
  QuotaExceededHitsBucket,
  QuotaExceededHitsResponse,
} from "@voice2mail/shared/v2m_api";

import { MetricCard } from "./MetricCard";

interface QuotaExceededHitsProps {
  data: QuotaExceededHitsResponse;
}

function BucketRow({ title, bucket }: { title: string; bucket: QuotaExceededHitsBucket }) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <MetricCard label="Różnych osób" value={bucket.distinctUsers} />
        <MetricCard label="Uderzeń łącznie" value={bucket.totalEvents} />
      </div>
    </div>
  );
}

/**
 * Sygnał #1 (checkpoint 2026-07-25-v2m-projekt-metryk-12-sygnalow.md,
 * V2M-DEV-012): "pierwsze uderzenie w limit". Decyzja, którą to umożliwia —
 * "czy nie sprzedajemy, bo nie chcą płacić, czy bo nigdy nie docierają do
 * ściany" — dwie przeciwne interwencje. Endpoint
 * `GET /admin/metrics/quota-exceeded-hits` istniał wyłącznie jako forward do
 * Plausible (agregat, bez modelu per-użytkownik) — ten komponent konsumuje
 * pierwszą stronną kolekcję `quota_exceeded_hits`, tą samą tożsamość
 * użytkownika co M-A/M-B/M-C.
 *
 * Dwie liczby na kubełek, nigdy jedna — dziesięć uderzeń jednego użytkownika
 * to inny sygnał niż jedno uderzenie u dziesięciu różnych osób.
 *
 * `hoursSinceLastEventEver`: `null` = `quota_exceeded_hits` NIGDY nie
 * dostała zapisu (najgłośniejsze możliwe ustalenie); liczba = pipeline żyje.
 * Ten sam wzorzec co `RecordingCapHits` — patrz ten komponent po pełne
 * uzasadnienie odróżnienia "cicho w oknie" od "nigdy nie zapisano".
 */
export function QuotaExceededHits({ data }: QuotaExceededHitsProps) {
  if (data.hoursSinceLastEventEver === null) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
        <div className="text-red-800 text-sm font-medium">
          NIGDY nie zapisano do <code>quota_exceeded_hits</code> — ani razu, w całej historii, nie
          tylko w ostatnie {data.days} {data.days === 1 ? "dobę" : "dni"}. To nie jest zero uderzeń,
          to brak pomiaru.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">
        Ostatnie {data.days} {data.days === 1 ? "doba" : "dni"} — ile razy użytkownik uderzył w
        limit generacji (dostał 402), rozbite na poziom konta.
      </p>
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <MetricCard
          label="Godzin od ostatniego uderzenia (kiedykolwiek)"
          value={data.hoursSinceLastEventEver.toFixed(1)}
        />
      </div>
      <BucketRow title="Wszystkie konta — uderzenia w limit" bucket={data.overall} />
      <BucketRow title="Free" bucket={data.free} />
      <BucketRow title="Płatne" bucket={data.paid} />
    </div>
  );
}
